// Platform-owner-only dashboard section. Renders only when the current
// user is a super-admin (admin role + tenant.is_platform_owner). Sits above
// the regular per-tenant dashboard in Dashboard.js so Identium gets a
// SaaS-CEO snapshot every time they log in: how many companies they have,
// what plans, what's expiring, who came in last, total users/assets.
//
// All numbers come from /api/system/platform-stats which aggregates across
// every active tenant DB. Auto-refreshes when the user lands on Dashboard
// — no live socket because this view changes slowly (a few onboardings/day
// at most).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdBusiness, MdCheckCircle, MdBlock, MdArchive,
  MdPeople, MdInventory2, MdTrendingUp, MdSchedule, MdRefresh,
} from 'react-icons/md';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

export default function PlatformStats() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isSuperAdmin = user?.role === 'admin' && !!user?.tenant?.is_platform_owner;

  const reload = async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const r = await api.get('/system/platform-stats');
      setStats(r.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load platform stats');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [isSuperAdmin]);

  if (!isSuperAdmin) return null;
  if (loading && !stats) {
    return (
      <div className="card" style={{ padding: 16, marginBottom: 16, color: '#888' }}>
        Loading platform stats…
      </div>
    );
  }
  if (error) {
    return (
      <div className="alert alert-danger" style={{ marginBottom: 16 }}>
        {error}
      </div>
    );
  }
  if (!stats) return null;

  const { total, byStatus, byPlan, newLast30Days, totalUsers, totalAssets,
          recentlyCreated, trialsExpiringSoon, tenantsWithErrors, asOf } = stats;

  return (
    <section style={{ marginBottom: 24 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{
          fontSize: 11, fontWeight: 800, color: 'white', letterSpacing: 1,
          background: 'linear-gradient(135deg, #6a1b9a, #c2185b)',
          padding: '3px 10px', borderRadius: 4, textTransform: 'uppercase',
        }}>Platform Owner</span>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>
          Companies overview
        </h2>
        <span style={{ flex: 1 }} />
        <button onClick={reload} className="btn btn-outline btn-sm" title="Refresh">
          <MdRefresh /> Refresh
        </button>
      </div>

      {/* Top tiles row — totals at a glance */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Tile
          icon={<MdBusiness />} accent="#1565c0"
          label="Total companies" value={total}
          sub={`${newLast30Days} added in last 30 days`}
          onClick={() => nav('/companies')}
        />
        <Tile
          icon={<MdCheckCircle />} accent="#2e7d32"
          label="Active" value={byStatus.active || 0}
          sub={total ? `${Math.round((byStatus.active / total) * 100)}% of total` : ''}
        />
        <Tile
          icon={<MdBlock />} accent="#ef6c00"
          label="Suspended" value={byStatus.suspended || 0}
          sub="Locked out, recoverable"
        />
        <Tile
          icon={<MdArchive />} accent="#78909c"
          label="Archived" value={byStatus.archived || 0}
          sub="DB preserved, off-line"
        />
        <Tile
          icon={<MdPeople />} accent="#6a1b9a"
          label="Total users (active tenants)" value={totalUsers.toLocaleString()}
          sub="Sum across every active company"
        />
        <Tile
          icon={<MdInventory2 />} accent="#5d4037"
          label="Total assets (active tenants)" value={totalAssets.toLocaleString()}
          sub="Sum across every active company"
        />
      </div>

      {/* Two-column row — plans + trials expiring */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 12, marginBottom: 16 }}>
        {/* Plan breakdown */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <MdTrendingUp style={{ color: '#1565c0' }} />
            <h3 style={{ margin: 0, fontSize: 14, color: '#37474f' }}>Plans in use</h3>
          </div>
          {byPlan.length === 0 ? (
            <div style={{ fontSize: 13, color: '#888' }}>No companies onboarded yet.</div>
          ) : (
            <div>
              {byPlan
                .slice()
                .sort((a, b) => b.count - a.count)
                .map(p => {
                  const pct = total ? (p.count / total) * 100 : 0;
                  return (
                    <div key={p.plan} style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 2 }}>
                        <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{p.plan}</span>
                        <span style={{ color: '#666' }}>{p.count} {p.count === 1 ? 'company' : 'companies'}</span>
                      </div>
                      <div style={{ background: '#eef1f6', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          background: planColor(p.plan), height: '100%',
                          width: `${pct}%`, transition: 'width 0.3s',
                        }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Trials / subscriptions expiring soon */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <MdSchedule style={{ color: '#ef6c00' }} />
            <h3 style={{ margin: 0, fontSize: 14, color: '#37474f' }}>Expiring in the next 7 days</h3>
          </div>
          {trialsExpiringSoon.length === 0 ? (
            <div style={{ fontSize: 13, color: '#888' }}>
              Nothing expiring soon — all active companies are either evergreen or have &gt;7 days left.
            </div>
          ) : (
            <div>
              {trialsExpiringSoon.map(t => (
                <div
                  key={t.id}
                  onClick={() => nav('/companies')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0', borderBottom: '1px solid #eef1f6', cursor: 'pointer',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>
                      <code>{t.slug}</code> · {t.plan}
                      {t.contact_email && <> · {t.contact_email}</>}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 12, fontWeight: 700,
                    color: t.days_remaining <= 2 ? '#c62828' : '#ef6c00',
                    background: t.days_remaining <= 2 ? '#ffebee' : '#fff8e1',
                    padding: '4px 10px', borderRadius: 12,
                  }}>
                    {t.days_remaining}d left
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent onboardings */}
      <div className="card" style={{ padding: 16, marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <MdBusiness style={{ color: '#1565c0' }} />
          <h3 style={{ margin: 0, fontSize: 14, color: '#37474f' }}>Recently onboarded</h3>
          <span style={{ flex: 1 }} />
          <button onClick={() => nav('/companies')} className="btn btn-outline btn-sm">View all</button>
        </div>
        {recentlyCreated.length === 0 ? (
          <div style={{ fontSize: 13, color: '#888' }}>No companies yet.</div>
        ) : (
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <th style={{ padding: '6px 8px' }}>Slug</th>
                <th style={{ padding: '6px 8px' }}>Name</th>
                <th style={{ padding: '6px 8px' }}>Plan</th>
                <th style={{ padding: '6px 8px' }}>Status</th>
                <th style={{ padding: '6px 8px' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {recentlyCreated.map(t => (
                <tr key={t.id} style={{ borderTop: '1px solid #eef1f6' }}>
                  <td style={{ padding: '8px' }}>
                    <code style={{ fontSize: 12 }}>{t.slug}</code>
                    {t.is_platform_owner && (
                      <span style={{
                        marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#fff',
                        background: '#6a1b9a', padding: '1px 5px', borderRadius: 3,
                      }}>YOU</span>
                    )}
                  </td>
                  <td style={{ padding: '8px', fontWeight: 600 }}>{t.name}</td>
                  <td style={{ padding: '8px', textTransform: 'capitalize' }}>{t.plan}</td>
                  <td style={{ padding: '8px' }}>
                    <span className={`badge ${
                      t.status === 'active' ? 'badge-success' :
                      t.status === 'suspended' ? 'badge-warning' : 'badge-secondary'
                    }`}>{t.status}</span>
                  </td>
                  <td style={{ padding: '8px', color: '#666' }}>{(t.created_at || '').slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Tenant DB errors (rare — surfaced so Identium can fix them fast) */}
      {tenantsWithErrors && tenantsWithErrors.length > 0 && (
        <div className="alert alert-danger" style={{ marginBottom: 8 }}>
          <strong>{tenantsWithErrors.length} tenant DB(s) failed to read:</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {tenantsWithErrors.map(e => (
              <li key={e.slug}><code>{e.slug}</code>: {e.error}</li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ fontSize: 10, color: '#aaa', textAlign: 'right' }}>
        as of {new Date(asOf).toLocaleString()}
      </div>
    </section>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
function Tile({ icon, accent, label, value, sub, onClick }) {
  return (
    <div
      className="card"
      onClick={onClick}
      style={{
        padding: 14, cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s, box-shadow 0.15s',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}
      onMouseEnter={(e) => { if (onClick) e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { if (onClick) e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 30, height: 30, borderRadius: 6,
          background: accent + '22', color: accent,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
        }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#546e7a', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--primary)', lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: '#888' }}>{sub}</div>}
    </div>
  );
}

function planColor(plan) {
  const map = {
    trial: '#ef6c00',
    standard: '#1565c0',
    enterprise: '#2e7d32',
  };
  return map[plan] || '#6a1b9a';
}
